// 外見動画のエンコード仕様（PRizmo プロファイル）。ffmpeg（WASM 版の x264）に渡す引数と、区間並列の分け方を決める純関数
import { calcTargetSize, calcCropRect, calcTargetFrameRate, specKindOf } from './videoOptimizeSpec';

// どの PC でも同じ出力にするため、エンコーダは版とハッシュを固定する（API 側 encoder_core.py と同期させること）
export const ENCODER_CORE = {
    version: '0.12.10',
    files: {
        core: { name: 'ffmpeg-core.js', sha256: 'b266ab5b952555881dd6310663986994a182acb2b7ff25cf10a25f7a37ac2b21', type: 'text/javascript' },
        wasm: { name: 'ffmpeg-core.wasm', sha256: '9f57947a5bd530d8f00c5b3f2cb2a3492faa7e5d823315342d6a8656d0a6b7b7', type: 'application/wasm' },
    },
};

export const CRF = 20;
export const PRESET = 'slow';
// 質感を足す処理（psy-rd）を止めて元動画に近い見た目にする。どの種別でも画質がそろい、変換も少し速い
export const TUNE = 'ssim';
// キーフレーム間隔＝区間の長さ。区間ごとに独立してエンコードできるよう固定する
export const GOP_SEC = 5;
export const MAX_ENCODE_WORKERS = 6;

/** エンコード Worker の数。変換中も PC の操作が重くならないよう論理コアの半分まで */
export const calcEncodeWorkers = (cores) => Math.min(MAX_ENCODE_WORKERS, Math.max(1, Math.floor((cores || 0) / 2)));

/** NTSC 系（29.97 など）は誤差が出ないよう分数で表す */
export const fpsRational = (fps) => {
    if (Number.isInteger(fps)) return String(fps);
    const n = Math.round(fps * 1001);
    if (Math.abs(fps * 1001 - n) < 1e-6 && n % 1000 === 0) return `${n}/1001`;
    return String(Math.round(fps));
};

const fpsFraction = (text) => {
    const [num, den = '1'] = text.split('/');
    return { num: Number(num), den: Number(den) };
};

const evenDown = (v) => Math.floor(v / 2) * 2;
const floorEven = (v) => Math.max(2, evenDown(v));

/** GOP ごとの区間 */
export const planChunks = (frames, gop) => {
    const chunks = [];
    for (let start = 0; start < frames; start += gop) chunks.push({ index: chunks.length, startFrame: start, frames: Math.min(gop, frames - start) });
    return chunks;
};

/**
 * 1本の変換計画
 * @param meta { width, height, squareWidth, squareHeight（いずれも回転前）, rotation, frameRate, duration（秒） }
 */
export const planEncode = (meta, slotKey) => {
    const swapped = meta.rotation % 180 !== 0;
    // ffmpeg は回転を自動で適用するので、回転後の向きで寸法を決める
    const w = swapped ? meta.height : meta.width;
    const h = swapped ? meta.width : meta.height;
    const sqW = swapped ? meta.squareHeight : meta.squareWidth;
    const sqH = swapped ? meta.squareWidth : meta.squareHeight;
    const target = calcTargetSize(sqW, sqH, slotKey);

    // 切り抜きは正方画素の空間で決めて、デコード画素の座標に戻す（yuv420p なので偶数に揃える）
    const sq = calcCropRect(sqW, sqH, target);
    const cropW = Math.min(w, floorEven(sq.width * (w / sqW)));
    const cropH = Math.min(h, floorEven(sq.height * (h / sqH)));
    const cropped = cropW !== w || cropH !== h;
    const cropX = evenDown((w - cropW) / 2);
    const cropY = evenDown((h - cropH) / 2);

    const fps = calcTargetFrameRate(meta.frameRate);
    const fpsText = fpsRational(fps);
    const frames = Math.max(0, Math.round(meta.duration * fps));
    const gop = Math.max(1, Math.round(fps * GOP_SEC));
    const filters = [
        `fps=${fpsText}`,
        ...(cropped ? [`crop=${cropW}:${cropH}:${cropX}:${cropY}`] : []),
        // 寸法が同じでも通して、色を BT.709・tv レンジにそろえる
        `scale=${target.width}:${target.height}:flags=lanczos+accurate_rnd:out_color_matrix=bt709:out_range=tv`,
        'setsar=1',
        'format=yuv420p',
    ].join(',');

    return {
        target, fps, fpsText, frames, gop, filters,
        mini: specKindOf(slotKey) === 'mini',
        resized: cropped || target.width !== w || target.height !== h || sqW !== w || sqH !== h,
        chunks: planChunks(frames, gop),
    };
};

/** 1区間をエンコードする ffmpeg の引数（区間の先頭にシークし、フレーム数で止める） */
export const buildChunkArgs = (plan, chunk, input, output) => {
    const { num, den } = fpsFraction(plan.fpsText);
    const seek = chunk.startFrame > 0 ? ['-ss', ((chunk.startFrame * den) / num).toFixed(6)] : [];
    return [
        ...seek, '-i', input, '-map', '0:v:0', '-an', '-sn', '-dn', '-frames:v', String(chunk.frames), '-vf', plan.filters,
        '-c:v', 'libx264', '-preset', PRESET, '-tune', TUNE, '-crf', String(CRF), '-profile:v', 'high', ...(plan.mini ? [] : ['-level:v', '4.0']), '-pix_fmt', 'yuv420p',
        '-g', String(plan.gop), '-keyint_min', String(plan.gop), '-sc_threshold', '0', '-threads', '1',
        '-color_primaries', 'bt709', '-color_trc', 'bt709', '-colorspace', 'bt709', '-color_range', 'tv',
        '-map_metadata', '-1', '-fflags', '+bitexact', '-flags:v', '+bitexact', '-f', 'mp4', output,
    ];
};

/** 区間の MP4 をコピーだけで連結する（生の H.264 をつなぐと B フレームの時刻が壊れる） */
export const buildConcatArgs = (list, output) => [
    '-f', 'concat', '-safe', '0', '-i', list, '-c', 'copy', '-map_metadata', '-1', '-fflags', '+bitexact', '-movflags', '+faststart', '-f', 'mp4', output,
];

export const buildConcatList = (names) => names.map((n) => `file '${n}'\n`).join('');
